/**
 * LLM Configuration Tab Component
 *
 * Manages organization-level LLM API keys and budget settings.
 * Supports Anthropic (Claude), OpenAI (GPT), and Google (Gemini).
 */

import { useState, useEffect } from "react"
import { Key, Eye, EyeOff, Check, X, Loader2, DollarSign, Trash2 } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Progress } from "@/components/ui/progress"
import { toast } from "sonner"
import { llmConfigService, LLMConfig } from "@/services/llm-config"

interface LLMConfigTabProps {
  organizationId: number
}

interface ProviderConfig {
  id: 'anthropic' | 'openai' | 'google'
  name: string
  displayName: string
  placeholder: string
  docsUrl: string
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'anthropic',
    name: 'Anthropic',
    displayName: 'Claude (Anthropic)',
    placeholder: 'sk-ant-...',
    docsUrl: 'https://console.anthropic.com/',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    displayName: 'GPT (OpenAI)',
    placeholder: 'sk-...',
    docsUrl: 'https://platform.openai.com/api-keys',
  },
  {
    id: 'google',
    name: 'Google',
    displayName: 'Gemini (Google)',
    placeholder: 'AIza...',
    docsUrl: 'https://aistudio.google.com/apikey',
  },
]

export function LLMConfigTab({ organizationId }: LLMConfigTabProps) {
  // Config state
  const [config, setConfig] = useState<LLMConfig | null>(null)
  const [loading, setLoading] = useState(true)

  // API Key input state
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({
    anthropic: '',
    openai: '',
    google: '',
  })
  const [keyVisible, setKeyVisible] = useState<Record<string, boolean>>({
    anthropic: false,
    openai: false,
    google: false,
  })
  const [testing, setTesting] = useState<Record<string, boolean>>({
    anthropic: false,
    openai: false,
    google: false,
  })
  const [saving, setSaving] = useState<Record<string, boolean>>({
    anthropic: false,
    openai: false,
    google: false,
  })

  // Budget state
  const [dailyBudget, setDailyBudget] = useState<string>('')
  const [monthlyBudget, setMonthlyBudget] = useState<string>('')
  const [budgetSaving, setBudgetSaving] = useState(false)

  // Settings state
  const [settingsSaving, setSettingsSaving] = useState(false)

  // Pending provider selection (not saved until API key is tested)
  const [pendingProvider, setPendingProvider] = useState<string | null>(null)

  // Track if API key has been tested and is valid
  const [keyTested, setKeyTested] = useState<Record<string, boolean>>({
    anthropic: false,
    openai: false,
    google: false,
  })

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true)
      try {
        const data = await llmConfigService.getConfig(organizationId)
        setConfig(data)
        setDailyBudget(data.daily_budget_usd.toString())
        setMonthlyBudget(data.monthly_budget_usd.toString())
      } catch (error) {
        console.error('Failed to load LLM config:', error)
        toast.error('Failed to load LLM configuration')
      } finally {
        setLoading(false)
      }
    }

    loadConfig()
  }, [organizationId])

  // Test API key
  const handleTestKey = async (provider: 'anthropic' | 'openai' | 'google') => {
    const key = apiKeys[provider]
    if (!key.trim()) {
      toast.error('Please enter an API key to test')
      return
    }

    setTesting((prev) => ({ ...prev, [provider]: true }))
    try {
      const result = await llmConfigService.testApiKey(organizationId, provider, key)
      if (result.valid) {
        toast.success(`${PROVIDERS.find((p) => p.id === provider)?.name} API key is valid`)
      } else {
        toast.error(`Invalid API key: ${result.message}`)
      }
    } catch (error) {
      console.error('Failed to test API key:', error)
      toast.error('Failed to test API key')
    } finally {
      setTesting((prev) => ({ ...prev, [provider]: false }))
    }
  }

  // Save API key
  const handleSaveKey = async (provider: 'anthropic' | 'openai' | 'google') => {
    const key = apiKeys[provider]
    if (!key.trim()) {
      toast.error('Please enter an API key to save')
      return
    }

    setSaving((prev) => ({ ...prev, [provider]: true }))
    try {
      await llmConfigService.setApiKey(organizationId, provider, key)
      toast.success(`${PROVIDERS.find((p) => p.id === provider)?.name} API key saved`)
      // Refresh config
      const data = await llmConfigService.getConfig(organizationId)
      setConfig(data)
      // Clear the input
      setApiKeys((prev) => ({ ...prev, [provider]: '' }))
    } catch (error) {
      console.error('Failed to save API key:', error)
      toast.error('Failed to save API key')
    } finally {
      setSaving((prev) => ({ ...prev, [provider]: false }))
    }
  }

  // Delete API key
  const handleDeleteKey = async (provider: 'anthropic' | 'openai' | 'google') => {
    setSaving((prev) => ({ ...prev, [provider]: true }))
    try {
      await llmConfigService.deleteApiKey(organizationId, provider)
      toast.success(`${PROVIDERS.find((p) => p.id === provider)?.name} API key deleted`)
      // Refresh config
      const data = await llmConfigService.getConfig(organizationId)
      setConfig(data)
    } catch (error) {
      console.error('Failed to delete API key:', error)
      toast.error('Failed to delete API key')
    } finally {
      setSaving((prev) => ({ ...prev, [provider]: false }))
    }
  }

  // Save budget
  const handleSaveBudget = async () => {
    const daily = parseFloat(dailyBudget)
    const monthly = parseFloat(monthlyBudget)

    if (isNaN(daily) || daily < 0) {
      toast.error('Please enter a valid daily budget')
      return
    }
    if (isNaN(monthly) || monthly < 0) {
      toast.error('Please enter a valid monthly budget')
      return
    }

    setBudgetSaving(true)
    try {
      await llmConfigService.updateBudget(organizationId, daily, monthly)
      toast.success('Budget settings saved')
      // Refresh config
      const data = await llmConfigService.getConfig(organizationId)
      setConfig(data)
    } catch (error) {
      console.error('Failed to save budget:', error)
      toast.error('Failed to save budget settings')
    } finally {
      setBudgetSaving(false)
    }
  }

  // Update settings
  const handleSettingsChange = async (
    field: 'default_provider' | 'is_active' | 'budget_exceeded_action',
    value: string | boolean
  ) => {
    setSettingsSaving(true)
    try {
      await llmConfigService.updateSettings(organizationId, { [field]: value })
      toast.success('Settings updated')
      // Refresh config
      const data = await llmConfigService.getConfig(organizationId)
      setConfig(data)
    } catch (error) {
      console.error('Failed to update settings:', error)
      toast.error('Failed to update settings')
    } finally {
      setSettingsSaving(false)
    }
  }

  // Check if provider has a key configured
  const hasKey = (provider: 'anthropic' | 'openai' | 'google'): boolean => {
    if (!config) return false
    switch (provider) {
      case 'anthropic':
        return config.has_anthropic_key
      case 'openai':
        return config.has_openai_key
      case 'google':
        return config.has_google_key
    }
  }

  // Calculate budget usage percentages
  const dailyUsagePercent = config
    ? Math.min((config.current_day_spend_usd / config.daily_budget_usd) * 100, 100)
    : 0
  const monthlyUsagePercent = config
    ? Math.min((config.current_month_spend_usd / config.monthly_budget_usd) * 100, 100)
    : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Handle provider change - only updates local state, no API calls
  const handleProviderChange = (value: string) => {
    setPendingProvider(value)
    // Reset key tested state when changing provider
    setKeyTested({
      anthropic: false,
      openai: false,
      google: false,
    })
  }

  // Get current selector value
  const getProviderValue = () => {
    if (pendingProvider !== null) return pendingProvider
    if (!config?.is_active) return 'none'
    return config?.default_provider ?? 'anthropic'
  }

  // Get the provider to show in API key section
  const getActiveProvider = () => {
    const value = getProviderValue()
    if (value === 'none') return null
    return value
  }

  // Test API key and mark as tested
  const handleTestKeyWithValidation = async (provider: 'anthropic' | 'openai' | 'google') => {
    const key = apiKeys[provider]
    if (!key.trim()) {
      toast.error('Please enter an API key to test')
      return
    }

    setTesting((prev) => ({ ...prev, [provider]: true }))
    try {
      const result = await llmConfigService.testApiKey(organizationId, provider, key)
      if (result.valid) {
        toast.success(`${PROVIDERS.find((p) => p.id === provider)?.name} API key is valid`)
        setKeyTested((prev) => ({ ...prev, [provider]: true }))
      } else {
        toast.error(`Invalid API key: ${result.message}`)
        setKeyTested((prev) => ({ ...prev, [provider]: false }))
      }
    } catch (error) {
      console.error('Failed to test API key:', error)
      toast.error('Failed to test API key')
      setKeyTested((prev) => ({ ...prev, [provider]: false }))
    } finally {
      setTesting((prev) => ({ ...prev, [provider]: false }))
    }
  }

  // Save all settings (provider, API key if tested, budget)
  const handleSaveAll = async () => {
    const selectedProvider = getProviderValue()

    setBudgetSaving(true)
    try {
      // Save budget first
      const daily = parseFloat(dailyBudget)
      const monthly = parseFloat(monthlyBudget)

      if (!isNaN(daily) && daily >= 0 && !isNaN(monthly) && monthly >= 0) {
        await llmConfigService.updateBudget(organizationId, daily, monthly)
      }

      // Handle provider/AI settings
      if (selectedProvider === 'none') {
        // Turn off AI
        await llmConfigService.updateSettings(organizationId, { is_active: false })
      } else {
        const provider = selectedProvider as 'anthropic' | 'openai' | 'google'
        const key = apiKeys[provider]

        // If there's a new key that was tested, save it
        if (key.trim() && keyTested[provider]) {
          await llmConfigService.setApiKey(organizationId, provider, key)
        }

        // Check if provider has a key (existing or just saved)
        const providerHasKey = hasKey(provider) || (key.trim() && keyTested[provider])

        if (providerHasKey) {
          // Activate with this provider
          await llmConfigService.updateSettings(organizationId, {
            is_active: true,
            default_provider: provider
          })
        } else {
          toast.error(`Please enter and test a valid ${PROVIDERS.find(p => p.id === provider)?.name} API key before saving`)
          setBudgetSaving(false)
          return
        }
      }

      toast.success('Settings saved')

      // Refresh config and clear state
      const data = await llmConfigService.getConfig(organizationId)
      setConfig(data)
      setApiKeys({ anthropic: '', openai: '', google: '' })
      setKeyTested({ anthropic: false, openai: false, google: false })
      setPendingProvider(null)
    } catch (error) {
      console.error('Failed to save settings:', error)
      toast.error('Failed to save settings')
    } finally {
      setBudgetSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* AI Provider */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>AI Provider</CardTitle>
            {config?.is_active ? (
              <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">Disabled</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select
            value={getProviderValue()}
            onValueChange={handleProviderChange}
            disabled={settingsSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select AI provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No AI</SelectItem>
              {PROVIDERS.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.displayName}
                  {hasKey(provider.id) && (
                    <Check className="ml-2 h-4 w-4 text-green-500 inline" />
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* API Key - Show when a provider is selected */}
          {(() => {
            const activeProviderId = getActiveProvider()
            if (!activeProviderId) return null

            const provider = PROVIDERS.find(p => p.id === activeProviderId)
            if (!provider) return null

            const isPending = pendingProvider !== null
            const providerHasKey = hasKey(provider.id)

            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">API Key</Label>
                    {providerHasKey ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                        <Check className="h-3 w-3 mr-1" />
                        Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-xs">
                        Required
                      </Badge>
                    )}
                  </div>
                  <a
                    href={provider.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Get {provider.name} key
                  </a>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      type={keyVisible[provider.id] ? 'text' : 'password'}
                      placeholder={
                        providerHasKey
                          ? `Enter new ${provider.name} key to replace`
                          : `${provider.placeholder}`
                      }
                      value={apiKeys[provider.id]}
                      onChange={(e) =>
                        setApiKeys((prev) => ({
                          ...prev,
                          [provider.id]: e.target.value,
                        }))
                      }
                      className="pr-10 font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() =>
                        setKeyVisible((prev) => ({
                          ...prev,
                          [provider.id]: !prev[provider.id],
                        }))
                      }
                    >
                      {keyVisible[provider.id] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant={keyTested[provider.id] ? "default" : "outline"}
                    onClick={() => handleTestKeyWithValidation(provider.id)}
                    disabled={!apiKeys[provider.id] || testing[provider.id]}
                    className={keyTested[provider.id] ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    {testing[provider.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : keyTested[provider.id] ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Valid
                      </>
                    ) : (
                      'Test'
                    )}
                  </Button>
                  {providerHasKey && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteKey(provider.id)}
                      disabled={saving[provider.id]}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )
          })()}
        </CardContent>
      </Card>

      {/* Budget Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Controls</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Usage with inline editable budgets */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Daily Spend</span>
                <span className="flex items-center gap-1">
                  ${config?.current_day_spend_usd.toFixed(2) ?? '0.00'} /
                  <span className="inline-flex items-center">
                    $<input
                      type="number"
                      min="0"
                      step="0.01"
                      value={dailyBudget}
                      onChange={(e) => setDailyBudget(e.target.value)}
                      className="w-16 bg-transparent border-b border-dashed border-muted-foreground/50 hover:border-primary focus:border-primary focus:outline-none text-right font-medium"
                    />
                  </span>
                </span>
              </div>
              <Progress value={dailyUsagePercent} className="h-2" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Monthly Spend</span>
                <span className="flex items-center gap-1">
                  ${config?.current_month_spend_usd.toFixed(2) ?? '0.00'} /
                  <span className="inline-flex items-center">
                    $<input
                      type="number"
                      min="0"
                      step="0.01"
                      value={monthlyBudget}
                      onChange={(e) => setMonthlyBudget(e.target.value)}
                      className="w-16 bg-transparent border-b border-dashed border-muted-foreground/50 hover:border-primary focus:border-primary focus:outline-none text-right font-medium"
                    />
                  </span>
                </span>
              </div>
              <Progress value={monthlyUsagePercent} className="h-2" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>When Budget Exceeded</Label>
            <Select
              value={config?.budget_exceeded_action ?? 'pause'}
              onValueChange={(value) =>
                handleSettingsChange('budget_exceeded_action', value)
              }
              disabled={settingsSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pause">
                  Pause agent runs (recommended)
                </SelectItem>
                <SelectItem value="notify">
                  Send notification only
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button onClick={handleSaveAll} disabled={budgetSaving} className="w-full">
        {budgetSaving ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          'Save'
        )}
      </Button>
    </div>
  )
}
